import { useMemo, useState } from "react";
import { db } from "./firebase";
import { collection, doc, setDoc, updateDoc, deleteDoc } from "firebase/firestore";
import PlayerSearchInput from "./PlayerSearchInput";
import { useResults } from "./lib/ResultsContext";
import staticMatches from "./darts-results.json";
import {
  STAGES, GRADES, deriveResults, isCountingTournament, cleanName, overrideKey, drawOf,
} from "./lib/rankings";
import { parsePaste, buildMatch, matchToPrefill, numberOrNull, makeCanon, canonicaliseMatches } from "./lib/resultsEntry";

var card = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,165,0,0.15)", borderRadius: 14, padding: "20px 22px" };
var inputStyle = { width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,165,0,0.2)", borderRadius: 8, color: "#fff", padding: "10px 14px", fontSize: 14, outline: "none", fontFamily: "sans-serif" };
var labelStyle = { fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 };
var btnStyle = { padding: "11px 18px", background: "linear-gradient(135deg,#FFB020,#FF6B00)", border: "none", borderRadius: 8, color: "#000", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "sans-serif" };
var btnSecondaryStyle = { padding: "8px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#ccc", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "sans-serif" };
var errorBox = { background: "rgba(255,80,80,0.1)", border: "1px solid rgba(255,80,80,0.3)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#ff8080", marginBottom: 10 };
var warnBox = { background: "rgba(255,176,32,0.08)", border: "1px solid rgba(255,176,32,0.3)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#FFB020", marginBottom: 8 };

var STATIC_KEYS = (function() {
  var keys = {};
  staticMatches.forEach(function(m) { keys[m.tournament + "|" + m.date] = true; });
  return keys;
})();

// ---- single match entry ---------------------------------------------------

function MatchEntryForm(props) {
  var initial = props.initial;
  var onAdd = props.onAdd;
  var [grade, setGrade] = useState(initial.grade);
  var [stage, setStage] = useState(initial.stage);
  var [group, setGroup] = useState(initial.group);
  var [p1, setP1] = useState(initial.p1);
  var [p2, setP2] = useState(initial.p2);
  var [s1, setS1] = useState(initial.s1);
  var [s2, setS2] = useState(initial.s2);
  var [a1, setA1] = useState(initial.a1);
  var [a2, setA2] = useState(initial.a2);
  var [error, setError] = useState("");

  function submit(e) {
    e.preventDefault();
    setError("");
    var n1 = parseInt(s1, 10);
    var n2 = parseInt(s2, 10);
    if (!cleanName(p1) || !cleanName(p2)) { setError("Enter both player names."); return; }
    if (cleanName(p1) === cleanName(p2)) { setError("A player can't play themselves."); return; }
    if (isNaN(n1) || isNaN(n2)) { setError("Enter both scores."); return; }
    if (n1 === n2) { setError("Scores can't be level — one player must win."); return; }
    onAdd(buildMatch(grade, stage, group, cleanName(p1), cleanName(p2), n1, n2, numberOrNull(a1), numberOrNull(a2)));
    // keep grade + stage (the next match is usually the same round)
    setP1(""); setP2(""); setS1(""); setS2(""); setA1(""); setA2(""); setGroup(group);
  }

  var numberInput = Object.assign({}, inputStyle, { textAlign: "center" });

  return (
    <form onSubmit={submit} style={{ border: "1px solid rgba(255,176,32,0.25)", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#FFB020", marginBottom: 10 }}>Add a match</div>
      {error && <div style={errorBox}>{error}</div>}
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 130 }}>
          <div style={labelStyle}>Grade</div>
          <select style={inputStyle} value={grade} onChange={function(e) { setGrade(e.target.value); }}>
            <option value="">No grade (club night)</option>
            {GRADES.map(function(g) { return <option key={g} value={g}>{g}</option>; })}
          </select>
        </div>
        <div style={{ flex: 2, minWidth: 180 }}>
          <div style={labelStyle}>Stage</div>
          <select style={inputStyle} value={stage} onChange={function(e) { setStage(e.target.value); }}>
            {STAGES.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
          </select>
        </div>
        {stage === "Round Robin" && (
          <div style={{ width: 90 }}>
            <div style={labelStyle}>Group</div>
            <input style={inputStyle} value={group} onChange={function(e) { setGroup(e.target.value); }} placeholder="A" />
          </div>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 70px 1fr", gap: 10, marginBottom: 10, alignItems: "end" }}>
        <div>
          <div style={labelStyle}>Player 1</div>
          <PlayerSearchInput value={p1} onSelect={setP1} placeholder="Search or type..." />
        </div>
        <div>
          <div style={labelStyle}>Score</div>
          <input style={numberInput} type="number" min="0" value={s1} onChange={function(e) { setS1(e.target.value); }} />
        </div>
        <div>
          <div style={labelStyle}>Score</div>
          <input style={numberInput} type="number" min="0" value={s2} onChange={function(e) { setS2(e.target.value); }} />
        </div>
        <div>
          <div style={labelStyle}>Player 2</div>
          <PlayerSearchInput value={p2} onSelect={setP2} placeholder="Search or type..." />
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ width: 130 }}>
          <div style={labelStyle}>P1 average</div>
          <input style={inputStyle} type="number" step="0.01" value={a1} onChange={function(e) { setA1(e.target.value); }} placeholder="optional" />
        </div>
        <div style={{ width: 130 }}>
          <div style={labelStyle}>P2 average</div>
          <input style={inputStyle} type="number" step="0.01" value={a2} onChange={function(e) { setA2(e.target.value); }} placeholder="optional" />
        </div>
        <button type="submit" style={btnStyle}>Add match</button>
      </div>
    </form>
  );
}

// ---- bulk paste -----------------------------------------------------------

function BulkPaste(props) {
  var knownNames = props.knownNames;
  var onAdd = props.onAdd;
  var [text, setText] = useState("");
  var [parsed, setParsed] = useState(null);
  var [defaultGrade, setDefaultGrade] = useState("Grade A");
  var [defaultStage, setDefaultStage] = useState("");
  var [draw, setDraw] = useState("auto");
  var allNames = useResults().playerNames;
  var canon = useMemo(function() { return makeCanon(allNames); }, [allNames]);

  var unknown = [];
  if (parsed) {
    var seen = {};
    parsed.matches.forEach(function(m) {
      [m.player1, m.player2].forEach(function(n) {
        if (!knownNames[n] && !seen[n]) { seen[n] = true; unknown.push(n); }
      });
    });
  }

  function check() {
    var result = parsePaste(text, { grade: defaultGrade, stage: defaultStage || null, draw: draw });
    result.matches = canonicaliseMatches(result.matches, canon);
    setParsed(result);
  }

  var small = Object.assign({}, inputStyle, { padding: "6px 10px", fontSize: 12 });

  return (
    <div style={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#ddd", marginBottom: 4 }}>Paste many matches at once</div>
      <p style={{ fontSize: 11, color: "#666", marginTop: 0, marginBottom: 8 }}>
        <strong style={{ color: "#999" }}>From DartConnect:</strong> select the results on the page, copy, and paste here — stages
        (Finals, Semi Finals, Group B…) and grades are picked up from the headings. {" "}
        <strong style={{ color: "#999" }}>Or a table:</strong> one match per line as{" "}
        <code>grade | stage | player 1 | player 2 | score 1 | score 2 | avg 1 | avg 2</code> (tabs, <code>|</code> or commas).
      </p>
      <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ minWidth: 170 }}>
          <div style={labelStyle}>Draw</div>
          <select style={small} value={draw} onChange={function(e) { setDraw(e.target.value); setParsed(null); }}>
            <option value="auto">Detect from the paste</option>
            <option value="Main">Main draw</option>
            <option value="Consolation">Consolation draw</option>
          </select>
        </div>
        <div style={{ minWidth: 130 }}>
          <div style={labelStyle}>Grade if not in paste</div>
          <select style={small} value={defaultGrade} onChange={function(e) { setDefaultGrade(e.target.value); setParsed(null); }}>
            <option value="">No grade (club night)</option>
            {GRADES.map(function(g) { return <option key={g} value={g}>{g}</option>; })}
          </select>
        </div>
        <div style={{ minWidth: 160 }}>
          <div style={labelStyle}>Stage if not in paste</div>
          <select style={small} value={defaultStage} onChange={function(e) { setDefaultStage(e.target.value); setParsed(null); }}>
            <option value="">— must be in paste —</option>
            {STAGES.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
          </select>
        </div>
      </div>
      <textarea
        value={text}
        onChange={function(e) { setText(e.target.value); setParsed(null); }}
        rows={6}
        placeholder={"Paste from DartConnect, or:  A | QF | Moore, Andy | Barnes, Gary | 4 | 2 | 58.2 | 54.1"}
        style={Object.assign({}, inputStyle, { fontFamily: "monospace", fontSize: 12, resize: "vertical" })}
      />
      <div style={{ marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" style={btnSecondaryStyle} onClick={check}>Check</button>
        {parsed && parsed.matches.length > 0 && (
          <button type="button" style={btnStyle} onClick={function() { onAdd(parsed.matches); setText(""); setParsed(null); }}>
            {"Add " + parsed.matches.length + " match" + (parsed.matches.length === 1 ? "" : "es")}
          </button>
        )}
        {parsed && parsed.format && (
          <span style={{ fontSize: 11, color: "#666" }}>{"Read as: " + parsed.format}</span>
        )}
      </div>

      {parsed && parsed.matches.length > 0 && (
        <div style={{ marginTop: 10, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 10px" }}>
          <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
            Understood — check before adding
          </div>
          {parsed.matches.slice(0, 40).map(function(m, i) {
            var p1Won = m.score1 > m.score2;
            return (
              <div key={i} style={{ fontSize: 12, padding: "2px 0", color: "#ccc" }}>
                <span style={{ color: "#666" }}>{(m.grade || "No grade") + " · " + m.stage + (m.group ? " " + m.group : "") + " — "}</span>
                <span style={{ color: p1Won ? "#FFB020" : "#ccc" }}>{m.player1}</span>
                {" " + m.score1 + "–" + m.score2 + " "}
                <span style={{ color: !p1Won ? "#FFB020" : "#ccc" }}>{m.player2}</span>
              </div>
            );
          })}
          {parsed.matches.length > 40 && <div style={{ fontSize: 11, color: "#666" }}>{"…and " + (parsed.matches.length - 40) + " more"}</div>}
        </div>
      )}
      {parsed && parsed.errors.length > 0 && (
        <div style={Object.assign({}, errorBox, { marginTop: 10, marginBottom: 0 })}>
          {parsed.errors.map(function(er, i) { return <div key={i}>{er}</div>; })}
        </div>
      )}
      {parsed && parsed.notes && parsed.notes.length > 0 && (
        <div style={Object.assign({}, warnBox, { marginTop: 10, marginBottom: 0 })}>
          {parsed.notes.map(function(n, i) { return <div key={i}>{n}</div>; })}
        </div>
      )}
      {parsed && unknown.length > 0 && (
        <div style={Object.assign({}, warnBox, { marginTop: 10, marginBottom: 0 })}>
          {"Not seen in any previous results (check the spelling): " + unknown.join("; ")}
        </div>
      )}
    </div>
  );
}

// ---- points preview -------------------------------------------------------

function PointsPreview(props) {
  var matches = props.matches;
  var overrides = props.overrides;
  var onOverrides = props.onOverrides;
  var counts = props.counts;

  var base = useMemo(function() { return deriveResults(matches, {}); }, [matches]);
  var applied = useMemo(function() { return deriveResults(matches, overrides); }, [matches, overrides]);

  var baseByKey = {};
  base.rows.forEach(function(r) { baseByKey[overrideKey(r.player, r.grade, r.draw)] = r.points; });

  var warnings = applied.warnings.slice();
  var finals = {};
  matches.forEach(function(m) {
    var s = cleanName(m.stage);
    if (s === "Round Robin" || !m.grade) return;
    var key = m.grade + "|" + drawOf(s);
    if (!finals[key]) finals[key] = { final: false };
    if (s === "Final" || s === "Consolation Final") finals[key].final = true;
  });
  Object.keys(finals).forEach(function(k) {
    if (!finals[k].final) warnings.push("No final recorded for " + k.replace("|", " – ") + " draw.");
  });

  function setPoints(row, value) {
    var key = overrideKey(row.player, row.grade, row.draw);
    var next = Object.assign({}, overrides);
    var n = parseInt(value, 10);
    if (value === "" || isNaN(n) || n === baseByKey[key]) delete next[key]; else next[key] = n;
    onOverrides(next);
  }

  var groups = {};
  applied.rows.forEach(function(r) {
    var k = r.grade + " · " + r.draw;
    (groups[k] = groups[k] || []).push(r);
  });
  var stageRank = {};
  STAGES.forEach(function(s, i) { stageRank[s] = i; });

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#FFB020", marginBottom: 6 }}>Ranking points preview</div>
      {!counts && <div style={warnBox}>This event is set not to count for rankings, so these points will not be applied.</div>}
      {warnings.map(function(w, i) { return <div key={i} style={warnBox}>{w}</div>; })}
      {applied.rows.length === 0 && <p style={{ fontSize: 12, color: "#666" }}>No knockout results yet — points appear as you add knockout matches.</p>}
      {Object.keys(groups).sort().map(function(k) {
        var rows = groups[k].slice().sort(function(a, b) { return b.points - a.points || stageRank[a.stage] - stageRank[b.stage] || a.player.localeCompare(b.player); });
        return (
          <div key={k} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{k}</div>
            {rows.map(function(r) {
              return (
                <div key={r.player + r.grade + r.draw} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 70px 90px", gap: 8, alignItems: "center", padding: "4px 0", borderBottom: "0.5px solid rgba(255,255,255,0.05)", fontSize: 12 }}>
                  <span style={{ color: "#ddd" }}>{r.player}</span>
                  <span style={{ color: "#777" }}>{r.stage}</span>
                  <span style={{ color: r.outcome === "W" ? "#6DC878" : "#888" }}>{r.outcome === "W" ? "Won" : "Lost"}</span>
                  <input
                    type="number"
                    value={r.points}
                    onChange={function(e) { setPoints(r, e.target.value); }}
                    style={{ width: "100%", boxSizing: "border-box", background: r.overridden ? "rgba(255,176,32,0.15)" : "rgba(255,255,255,0.05)", border: "1px solid " + (r.overridden ? "rgba(255,176,32,0.5)" : "rgba(255,255,255,0.1)"), borderRadius: 6, color: "#fff", padding: "4px 6px", fontSize: 12, textAlign: "right" }}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ---- editor for one result set -------------------------------------------

function ResultsEditor(props) {
  var initial = props.initial;
  var entryTournaments = props.entryTournaments;
  var knownNames = props.knownNames;
  var onClose = props.onClose;

  var [name, setName] = useState(initial.name);
  var [date, setDate] = useState(initial.date);
  var [counts, setCounts] = useState(initial.countsForRankings);
  var [countsTouched, setCountsTouched] = useState(!!initial.id);
  var [tournamentId, setTournamentId] = useState(initial.tournamentId || "");
  var [matches, setMatches] = useState(initial.matches);
  var [overrides, setOverrides] = useState(initial.pointOverrides);
  var [prefill, setPrefill] = useState({ grade: "Grade A", stage: "Round of 32", group: "", p1: "", p2: "", s1: "", s2: "", a1: "", a2: "", nonce: 0 });
  var [error, setError] = useState("");
  var [saving, setSaving] = useState(false);

  function changeName(v) {
    setName(v);
    if (!countsTouched) setCounts(isCountingTournament(v));
  }

  function linkTournament(id) {
    setTournamentId(id);
    var t = entryTournaments.filter(function(x) { return x.id === id; })[0];
    if (t) { changeName(t.name); setDate(t.date); }
  }

  var allNames = useResults().playerNames;
  var canon = useMemo(function() { return makeCanon(allNames); }, [allNames]);

  function addMatch(m) {
    var fixed = canonicaliseMatches([m], canon)[0];
    setMatches(function(list) { return list.concat([fixed]); });
  }
  function removeMatch(i) { setMatches(function(list) { return list.filter(function(_, idx) { return idx !== i; }); }); }
  function editMatch(i) {
    setPrefill(matchToPrefill(matches[i]));
    removeMatch(i);
  }

  var replacesStatic = STATIC_KEYS[cleanName(name) + "|" + date];

  function save() {
    setError("");
    if (!cleanName(name)) { setError("Give the tournament a name."); return; }
    if (!date) { setError("Choose the tournament date."); return; }
    if (matches.length === 0) { setError("Add at least one match first."); return; }
    setSaving(true);
    var data = {
      name: cleanName(name),
      date: date,
      countsForRankings: !!counts,
      tournamentId: tournamentId || null,
      matches: matches,
      pointOverrides: overrides,
      updatedAt: new Date().toISOString(),
    };
    var write = initial.id
      ? updateDoc(doc(db, "tournamentResults", initial.id), data)
      : setDoc(doc(collection(db, "tournamentResults")), Object.assign({ createdAt: new Date().toISOString() }, data));
    write
      .then(function() { onClose(); })
      .catch(function(err) { setError(err.message); setSaving(false); });
  }

  // matches grouped by grade for the review list
  var byGrade = {};
  matches.forEach(function(m, i) {
    var k = m.grade || "No grade";
    (byGrade[k] = byGrade[k] || []).push({ m: m, i: i });
  });

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: "#FFB020" }}>{initial.id ? "Edit results" : "New results"}</h3>
        <button style={btnSecondaryStyle} onClick={onClose}>Close</button>
      </div>
      {error && <div style={errorBox}>{error}</div>}

      {entryTournaments.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>Link to an entry tournament (optional — fills in name and date)</div>
          <select style={inputStyle} value={tournamentId} onChange={function(e) { linkTournament(e.target.value); }}>
            <option value="">— none —</option>
            {entryTournaments.map(function(t) { return <option key={t.id} value={t.id}>{t.name + " · " + t.date}</option>; })}
          </select>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 2, minWidth: 200 }}>
          <div style={labelStyle}>Tournament name</div>
          <input style={inputStyle} value={name} onChange={function(e) { changeName(e.target.value); }} placeholder="Saturday Open" />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <div style={labelStyle}>Date</div>
          <input style={inputStyle} type="date" value={date} onChange={function(e) { setDate(e.target.value); }} />
        </div>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: counts ? "#6DC878" : "#888", cursor: "pointer", marginBottom: 12 }}>
        <input type="checkbox" checked={counts} onChange={function(e) { setCounts(e.target.checked); setCountsTouched(true); }} />
        Counts towards the rolling rankings (untick for Thursday club nights)
      </label>
      {replacesStatic && <div style={warnBox}>A tournament with this name and date already exists in the historical results — saving will replace it.</div>}

      <MatchEntryForm key={prefill.nonce} initial={prefill} onAdd={addMatch} />
      <BulkPaste knownNames={knownNames} onAdd={function(list) { setMatches(function(m) { return m.concat(list); }); }} />

      <div style={{ fontSize: 13, fontWeight: 700, color: "#ddd", marginBottom: 6 }}>{"Matches entered (" + matches.length + ")"}</div>
      {matches.length === 0 && <p style={{ fontSize: 12, color: "#666" }}>Nothing added yet.</p>}
      {Object.keys(byGrade).sort().map(function(g) {
        return (
          <div key={g} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{g}</div>
            {byGrade[g].map(function(item) {
              var m = item.m;
              var p1Won = m.score1 > m.score2;
              return (
                <div key={item.i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "0.5px solid rgba(255,255,255,0.05)", fontSize: 12 }}>
                  <span style={{ color: "#666", minWidth: 150 }}>{m.stage + (m.group ? " " + m.group : "")}</span>
                  <span style={{ flex: 1, color: "#ddd" }}>
                    <span style={{ color: p1Won ? "#FFB020" : "#ccc" }}>{m.player1}</span>
                    {" " + m.score1 + " – " + m.score2 + " "}
                    <span style={{ color: !p1Won ? "#FFB020" : "#ccc" }}>{m.player2}</span>
                  </span>
                  <button style={Object.assign({}, btnSecondaryStyle, { padding: "3px 8px", fontSize: 11 })} onClick={function() { editMatch(item.i); }}>Edit</button>
                  <button style={Object.assign({}, btnSecondaryStyle, { padding: "3px 8px", fontSize: 11, color: "#ff8080", borderColor: "rgba(255,80,80,0.3)" })} onClick={function() { removeMatch(item.i); }}>×</button>
                </div>
              );
            })}
          </div>
        );
      })}

      <PointsPreview matches={matches} overrides={overrides} onOverrides={setOverrides} counts={counts} />

      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <button style={btnStyle} onClick={save} disabled={saving}>{saving ? "Saving..." : "Save results"}</button>
        <button style={btnSecondaryStyle} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// ---- list + entry point ---------------------------------------------------

export default function AdminResults(props) {
  var entryTournaments = props.entryTournaments || [];
  var results = useResults();
  var saved = results.tournaments.slice().sort(function(a, b) { return (b.date || "").localeCompare(a.date || ""); });
  var [editing, setEditing] = useState(null);

  // names that existed BEFORE the result set being edited — so genuinely new
  // players can be flagged while entering
  var knownNames = useMemo(function() {
    var map = {};
    staticMatches.forEach(function(m) { map[cleanName(m.player1)] = true; map[cleanName(m.player2)] = true; });
    var editingId = editing && editing.id;
    saved.forEach(function(t) {
      if (t.id === editingId) return;
      (t.matches || []).forEach(function(m) { map[cleanName(m.player1)] = true; map[cleanName(m.player2)] = true; });
    });
    return map;
  }, [saved.length, editing && editing.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function startNew() {
    setEditing({ id: null, name: "", date: "", countsForRankings: true, tournamentId: "", matches: [], pointOverrides: {} });
  }

  function remove(t) {
    if (!window.confirm("Delete the results for \"" + t.name + "\"? Rankings, averages and profiles will update straight away.")) return;
    deleteDoc(doc(db, "tournamentResults", t.id));
  }

  if (editing) {
    return (
      <ResultsEditor
        key={editing.id || "new"}
        initial={editing}
        entryTournaments={entryTournaments}
        knownNames={knownNames}
        onClose={function() { setEditing(null); }}
      />
    );
  }

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: "#FFB020" }}>Results</h3>
        <button style={btnStyle} onClick={startNew}>+ Enter tournament results</button>
      </div>
      <p style={{ fontSize: 12, color: "#666", marginTop: 0 }}>
        Results entered here appear straight away on Results, Rankings, Averages, Tournaments and player profiles.
      </p>
      {saved.length === 0 && <p style={{ fontSize: 13, color: "#666" }}>No tournaments entered through the app yet.</p>}
      {saved.map(function(t) {
        return (
          <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#ddd" }}>{t.name}</div>
              <div style={{ fontSize: 12, color: "#666" }}>
                {t.date + " · " + (t.matches || []).length + " matches · " + (t.countsForRankings ? "counts for rankings" : "club night")}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={btnSecondaryStyle} onClick={function() { setEditing({ id: t.id, name: t.name, date: t.date, countsForRankings: !!t.countsForRankings, tournamentId: t.tournamentId || "", matches: t.matches || [], pointOverrides: t.pointOverrides || {} }); }}>Edit</button>
              <button style={Object.assign({}, btnSecondaryStyle, { color: "#ff8080", borderColor: "rgba(255,80,80,0.3)" })} onClick={function() { remove(t); }}>Delete</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
